import { generateClient } from "aws-amplify/api";
import { listPendingAttributionTransactions } from "../../../graphql/custom_queries";

const client = generateClient();

// Transactions arrive from Plaid with `accountNumber` set to the literal
// string "PENDING" when Capital One's feed does not carry `account_owner`.
// That sentinel is the partition key on capOneTransactionsByCardByDate,
// so querying the index for it returns exactly the unattributed rows.
const PENDING_SENTINEL = "PENDING";

// AppSync caps items per response. Without paging this silently undercounts
// and reports a plausible-looking number that never grows, which is worse
// than reporting nothing.
const PAGE_LIMIT = 1000;
const MAX_PAGES = 20;

/**
 * Counts transactions still awaiting card attribution.
 *
 * Returns { count, oldestDate, truncated } — or null if the query failed,
 * so callers can distinguish "no pending rows" from "we could not tell".
 * Those two must not render the same way.
 */
export const fetchPendingAttributionCount = async () => {
  let count = 0;
  let oldestDate = null;
  let nextToken = null;
  let pages = 0;

  try {
    do {
      const response = await client.graphql({
        query: listPendingAttributionTransactions,
        variables: {
          accountNumber: PENDING_SENTINEL,
          sortDirection: "ASC",
          limit: PAGE_LIMIT,
          nextToken,
        },
      });

      const result = response?.data?.capOneTransactionsByCardByDate;
      const items = result?.items ?? [];

      count += items.length;

      // Items come back date-ascending, so the first item of the first
      // page is the oldest. Age matters more than volume here: three rows
      // sitting unattributed for a month is a worse signal than thirty
      // from this morning.
      if (oldestDate === null && items.length > 0) {
        oldestDate = items[0].date;
      }

      nextToken = result?.nextToken ?? null;
      pages += 1;
    } while (nextToken && pages < MAX_PAGES);

    return {
      count,
      oldestDate,
      truncated: Boolean(nextToken),
    };
  } catch (err) {
    console.error("fetchPendingAttributionCount error:", err);
    return null;
  }
};

export default fetchPendingAttributionCount;
